import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, Settings as SettingsIcon, Users, UserCog, LogOut, KeyRound, Loader2, UserCircle2, Briefcase, Activity, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const NavItem = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`flex items-center gap-3 px-4 py-3 font-mono-jb text-xs uppercase tracking-[0.15em] border-l-2 transition-colors duration-200 ${
      active
        ? "border-[#00F0FF] bg-[#00F0FF]/5 text-[#00F0FF]"
        : "border-transparent text-[#8B949E] hover:text-white hover:bg-white/5"
    }`}
  >
    <Icon size={16} strokeWidth={2} />
    {label}
  </Link>
);

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/.test(nw)) {
      toast.error("Password must be at least 8 characters with one capital letter and one special character");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw(""); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B0C10] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center gap-2"><KeyRound size={16} /> Change Password</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Current Password</label>
            <Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">New Password</label>
            <Input data-testid="cp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
            <p className="text-[10px] text-[#484F58] mt-1.5 font-mono-jb">Min 8 chars · 1 capital letter · 1 special character.</p>
          </div>
          <Button data-testid="cp-submit" onClick={submit} disabled={busy}
            className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
            {busy ? <Loader2 className="animate-spin" size={15} /> : "Update Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Shell({ children }) {
  const { pathname } = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [cpOpen, setCpOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#050505]">
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#0B0C10] flex flex-col fixed h-screen">
        <div className="px-5 py-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <img src="/logo.png" alt="Soverenx Systems" className="w-full max-w-[180px] object-contain" />
          </Link>
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#484F58] mt-2 font-mono-jb">config → design docs</div>
        </div>

        <nav className="flex flex-col py-4">
          <NavItem to="/" icon={LayoutGrid} label="Projects" active={pathname === "/" || pathname.startsWith("/project")} />
          <NavItem to="/jobs" icon={Briefcase} label="Jobs" active={pathname === "/jobs"} />
          <NavItem to="/discovery" icon={Compass} label="Discovery" active={pathname === "/discovery"} />
          {isAdmin && <NavItem to="/invites" icon={Users} label="Team Invites" active={pathname === "/invites"} />}
          {isAdmin && <NavItem to="/integrations" icon={Activity} label="Integrations" active={pathname === "/integrations"} />}
          {isAdmin && <NavItem to="/users" icon={UserCog} label="User Management" active={pathname === "/users"} />}
          {isAdmin && <NavItem to="/settings" icon={SettingsIcon} label="AI Settings" active={pathname === "/settings"} />}
        </nav>

        <div className="mt-auto border-t border-white/10 p-4 space-y-3">
          {user && (
            <div className="flex items-center gap-2" data-testid="current-user">
              <UserCircle2 size={26} className="text-[#00F0FF] shrink-0" />
              <div className="min-w-0">
                <div className="font-mono-jb text-xs text-white truncate">{user.email}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] font-mono-jb" style={{ color: isAdmin ? "#00FF41" : "#8B949E" }}>{user.role}</div>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button data-testid="change-password-button" onClick={() => setCpOpen(true)} variant="outline"
              className="flex-1 rounded-sm border-white/15 bg-transparent text-[#8B949E] hover:bg-white/5 hover:text-white font-mono-jb text-[10px] uppercase tracking-wider h-8">
              <KeyRound size={12} className="mr-1" /> Password
            </Button>
            <Button data-testid="logout-button" onClick={logout} variant="outline"
              className="flex-1 rounded-sm border-[#FF3B30]/30 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] font-mono-jb text-[10px] uppercase tracking-wider h-8">
              <LogOut size={12} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-h-screen">{children}</main>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
    </div>
  );
}
